/**
 * SSO Callback Handler Page
 * Receives OAuth tokens from SSO redirect and completes authentication
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '@/store';
import { setTokens, updateUser } from '@/store/slices/authSlice';
import { getApiBaseUrl } from '@/utils/apiBase';
import { storage } from '@/utils/storage';
import type { User } from '@/types';
import { getHomeRouteForRole } from '@/utils/homeRoute';
import { FiLoader, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

function parseAuthMePayload(body: unknown): User {
  if (body === null || typeof body !== 'object') {
    throw new Error('Invalid user profile response');
  }
  const data = (body as { data?: unknown }).data;
  if (data === null || typeof data !== 'object') {
    throw new Error('Invalid user profile response');
  }
  const { id, email, role } = data as { id?: unknown; email?: unknown; role?: unknown };
  if (typeof id !== 'string' || typeof email !== 'string' || typeof role !== 'string') {
    throw new Error('Invalid user profile response');
  }
  return data as User;
}

export default function SSOCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Completing sign in...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get parameters from URL
        const token = searchParams.get('token');
        const refreshToken = searchParams.get('refreshToken');
        const error = searchParams.get('error');
        const isNewUser = searchParams.get('isNewUser') === 'true';

        // Check for errors
        if (error) {
          setStatus('error');
          setMessage(error);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Validate tokens
        if (!token || !refreshToken) {
          setStatus('error');
          setMessage('Missing authentication tokens');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Store tokens
        storage.setAccessToken(token);
        storage.setRefreshToken(refreshToken);

        // Update Redux store
        dispatch(setTokens({ accessToken: token, refreshToken }));

        // Fetch user details
        const apiUrl = getApiBaseUrl();
        const response = await fetch(`${apiUrl}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user details');
        }

        const userData = await response.json();
        const user = parseAuthMePayload(userData);
        dispatch(updateUser(user));

        // Set success state
        setStatus('success');
        setMessage(isNewUser ? 'Welcome! Your account has been created.' : 'Successfully signed in!');

        setTimeout(
          () =>
            navigate(
              getHomeRouteForRole(
                user.role,
                user.tenant?.settings as Record<string, unknown> | undefined,
              ),
              { replace: true },
            ),
          1500,
        );
      } catch (error: any) {
        console.error('SSO callback error:', error);
        setStatus('error');
        setMessage(error.message || 'Authentication failed');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, dispatch, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="max-w-md w-full">
        <div className="bg-card rounded-2xl shadow-lg p-8">
          {/* Status Icon */}
          <div className="flex justify-center mb-6">
            {status === 'processing' && (
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 rounded-full border-4 border-brand/20"></div>
                <div className="absolute inset-0 rounded-full border-4 border-brand border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <FiLoader className="w-8 h-8 text-brand" />
                </div>
              </div>
            )}
            {status === 'success' && (
              <div className="w-20 h-20 bg-completed/10 rounded-full flex items-center justify-center">
                <FiCheckCircle className="w-10 h-10 text-completed" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-20 h-20 bg-blocked/10 rounded-full flex items-center justify-center">
                <FiAlertCircle className="w-10 h-10 text-blocked" />
              </div>
            )}
          </div>

          {/* Status Message */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-text-primary mb-2">
              {status === 'processing' && 'Signing you in...'}
              {status === 'success' && 'Success!'}
              {status === 'error' && 'Authentication Failed'}
            </h2>
            <p className="text-text-secondary">{message}</p>

            {status === 'error' && (
              <button
                onClick={() => navigate('/login')}
                className="mt-6 px-6 py-2 bg-brand text-white rounded-xl font-medium hover:bg-brand-dark transition-colors"
              >
                Return to Login
              </button>
            )}
          </div>

          {/* Loading Bar */}
          {status === 'processing' && (
            <div className="mt-8">
              <div className="h-1.5 bg-surface rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-brand to-blue-400 animate-pulse"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
